/**
 * Known provider starting points offered by the forms. They only fill in form fields; the
 * operator still reviews, gives a reason and (in production) gets approval before anything is used.
 */

import type { ConnectionDetail, ConnectionKind, ConnectionProtocol, IntegrationContext, IntegrationEnvironment, RestSettings } from '../../api/areas/integrations';

export const WEBNIC_ADAPTER = 'webnic';

/** WebNIC's published API addresses (docs/registries/webnic.md): the OT&E server uses the
 * first, the production server the second (ADR-018). */
export const WEBNIC_BASE_URLS: Record<IntegrationEnvironment, string> = {
  OTE: 'https://oteapi.webnic.cc',
  PRODUCTION: 'https://api.webnic.cc',
};

export function isWebnic(c: Pick<ConnectionDetail, 'protocol' | 'adapter'>): boolean {
  return c.protocol === 'REST' && c.adapter === WEBNIC_ADAPTER;
}

/** Whether this build offers the WebNIC integration. */
export function webnicAvailable(ctx: IntegrationContext): boolean {
  return ctx.rest_adapters.some((a) => a.key === WEBNIC_ADAPTER);
}

/** REST settings for WebNIC's address for a server of kind `env`, keeping `base`'s account and
 * timeouts. */
export function webnicRest(env: IntegrationEnvironment, base?: RestSettings | null): RestSettings {
  return {
    base_url: WEBNIC_BASE_URLS[env],
    auth_method: 'ADAPTER_DEFINED',
    auth_settings: {},
    account_id: base?.account_id ?? null,
    connect_timeout_ms: base?.connect_timeout_ms ?? 10_000,
    request_timeout_ms: base?.request_timeout_ms ?? 30_000,
  };
}

/** Both WebNIC addresses, for the "fill in the OT&E / production address" helper. */
export function webnicPresets(base?: RestSettings | null): { environment: IntegrationEnvironment; value: RestSettings }[] {
  return (['OTE', 'PRODUCTION'] as const).map((environment) => ({ environment, value: webnicRest(environment, base) }));
}

/** Starting points of the "add a connection" form. */
export interface ConnectionPreset {
  key: 'VNNIC_EPP' | 'WEBNIC';
  kind: ConnectionKind;
  protocol: ConnectionProtocol;
  adapter: string;
  code: string;
  name: string;
  spec: string;
  upstream: { legal_name: string; trade_name: string } | null;
}

export const CONNECTION_PRESETS: readonly ConnectionPreset[] = [
  {
    key: 'VNNIC_EPP',
    kind: 'REGISTRY',
    protocol: 'EPP',
    adapter: 'vnnic-epp',
    code: 'vnnic',
    name: 'VNNIC EPP Gateway',
    spec: '',
    upstream: null,
  },
  {
    key: 'WEBNIC',
    kind: 'UPSTREAM_REGISTRAR',
    protocol: 'REST',
    adapter: WEBNIC_ADAPTER,
    code: 'webnic',
    name: 'WebNIC',
    spec: 'WebNIC RESTful API v2',
    // The IANA registrar ID is left for the operator to enter from the IANA registrar list.
    upstream: { legal_name: 'Web Commerce Communications Limited', trade_name: 'WebNIC' },
  },
];
